import { apiClient } from '@/lib/api-client';
import type {
  School,
  SchoolViewExtra,
  SchoolCreate,
  SchoolUpdate,
  SchoolsSearchParams,
  SchoolResource,
  Course,
  Staff,
  Schedule,
  ScheduleResponse,
  ScheduleImageResponse,
  DraftScheduleResponse,
  ScheduleSettingsResponse,
  BlockRotation,
  SchoolYear,
  SchoolLink,
  CalendarEntry,
  CalendarDayInfo,
} from '@/types/api';

// AI Schedule Parsing Types
export interface AIParsedPeriod {
  name: string;
  startTime: string;
  endTime: string;
}

export interface AIParsedLunch {
  name: string;
  startTime: string;
  endTime: string;
}

export interface AIParsedSchedule {
  genId: string;
  id: string;
  url: string;
  title: string;
  periods: AIParsedPeriod[];
  lunches: AIParsedLunch[];
  isComplete: boolean;
}

interface AIParseFromImageResponse {
  genId?: string;
  scheduleData?: {
    scheduleTitles?: string[];
    schedules?: Array<{
      title?: string;
      periods?: Array<{
        period?: string;
        start?: string;
        end?: string;
        lunch?: Array<{
          wave?: string;
          start?: string;
          end?: string;
        }>;
      }>;
    }>;
  };
}

export const schoolsService = {
  /**
   * Search/list schools with pagination and filtering
   */
  async searchSchools(params: SchoolsSearchParams = {}) {
    const { data, headers } = await apiClient.get<School[]>('/v2/schools', {
      params: {
        ...params,
        paginate: params.paginate ?? true,
        meta: params.meta ?? true,
      },
    });

    return {
      schools: data,
      total: parseInt(headers['x-pagination-items'] || '0', 10),
      userCount: parseInt(headers['x-user-count'] || '0', 10),
    };
  },

  /**
   * Get school by ID with full details
   */
  async getSchoolById(
    schoolId: string,
    forDashboard = true
  ): Promise<SchoolViewExtra> {
    const { data } = await apiClient.get<SchoolViewExtra>(
      `/v2/schools/${schoolId}`,
      {
        params: { for_dashboard: forDashboard },
      }
    );
    return data;
  },

  /**
   * Get a specific resource for a school
   */
  async getSchoolResource<T = unknown>(
    schoolId: string,
    resource: SchoolResource,
    dashboardMode = true,
    includeUserGenerated = false
  ): Promise<T> {
    const params: Record<string, boolean> = {
      dashboard_mode: dashboardMode,
    };

    if (resource === 'courses' && includeUserGenerated) {
      params.include_user_generated = true;
    }

    const { data } = await apiClient.get<T>(
      `/v2/schools/${schoolId}/${resource}`,
      { params }
    );
    return data;
  },

  /**
   * Create a new school
   */
  async createSchool(schoolData: SchoolCreate): Promise<School> {
    // Transform camelCase to snake_case for API
    const apiData: any = {
      school_name: schoolData.schoolName,
      school_title: schoolData.schoolTitle,
      status: schoolData.status,
      domains: schoolData.domains || [],
      timezone: schoolData.timezone,
      state: schoolData.state || '',
      country: schoolData.country || 'US',
    };

    // Optional fields - use !== undefined to allow empty strings / zero / false
    if (schoolData.lat !== undefined) apiData.lat = schoolData.lat;
    if (schoolData.lng !== undefined) apiData.lng = schoolData.lng;
    if (schoolData.website) apiData.website = schoolData.website;
    if (schoolData.phoneNumber) apiData.phone_number = schoolData.phoneNumber;
    if (schoolData.studentsCount !== undefined) apiData.students_count = schoolData.studentsCount;
    if (schoolData.primaryColor) apiData.primary_color = schoolData.primaryColor;
    if (schoolData.darkModeColor) apiData.dark_mode_color = schoolData.darkModeColor;
    if (schoolData.ncesCode) apiData.nces_code = schoolData.ncesCode;
    if (schoolData.liteLevel) apiData.lite_level = schoolData.liteLevel;
    if (schoolData.nickname) apiData.nickname = schoolData.nickname;
    if ((schoolData as any).private !== undefined) apiData.private = (schoolData as any).private;
    if ((schoolData as any).logoId) apiData.logo_id = (schoolData as any).logoId;

    // Canada-specific: don't send county_code/district_code/region for Canadian schools
    if (schoolData.country !== 'CA') {
      if (schoolData.districtCode) apiData.district_code = schoolData.districtCode;
      if (schoolData.countyCode) apiData.county_code = schoolData.countyCode;
      if (schoolData.region) apiData.region = schoolData.region;
    }

    const { data } = await apiClient.post<School>('/v2/schools', apiData);
    return data;
  },

  /**
   * Update an existing school
   */
  async updateSchool(
    schoolId: string,
    schoolData: SchoolUpdate
  ): Promise<School> {
    // Transform camelCase to snake_case for API
    const apiData: any = {};

    // Only include fields that are present in the update data
    if (schoolData.schoolTitle !== undefined) apiData.school_title = schoolData.schoolTitle;
    if (schoolData.status !== undefined) apiData.status = schoolData.status;
    if (schoolData.domains !== undefined) apiData.domains = schoolData.domains;
    if (schoolData.timezone !== undefined) apiData.timezone = schoolData.timezone;

    // Backend requires country when state is provided
    if (schoolData.state !== undefined) {
      apiData.state = schoolData.state;
      if (schoolData.country !== undefined) {
        apiData.country = schoolData.country;
      }
    }
    if (schoolData.country !== undefined) apiData.country = schoolData.country;

    if (schoolData.lat !== undefined) apiData.lat = schoolData.lat;
    if (schoolData.lng !== undefined) apiData.lng = schoolData.lng;
    if (schoolData.website !== undefined) apiData.website = schoolData.website;
    if (schoolData.phoneNumber !== undefined) apiData.phone_number = schoolData.phoneNumber;
    if (schoolData.studentsCount !== undefined) apiData.students_count = schoolData.studentsCount;
    if (schoolData.primaryColor !== undefined) apiData.primary_color = schoolData.primaryColor;
    if (schoolData.darkModeColor !== undefined) apiData.dark_mode_color = schoolData.darkModeColor;
    if (schoolData.ncesCode !== undefined) apiData.nces_code = schoolData.ncesCode;
    if (schoolData.liteLevel !== undefined) apiData.lite_level = schoolData.liteLevel;
    if (schoolData.nickname !== undefined) apiData.nickname = schoolData.nickname;
    if (schoolData.private !== undefined) apiData.private = schoolData.private;
    if (schoolData.logoId !== undefined) apiData.logo_id = schoolData.logoId;
    if (schoolData.notes !== undefined) apiData.notes = schoolData.notes;
    if ((schoolData as any).alphabeticLunchWaves !== undefined) {
      apiData.alphabetic_lunch_waves = (schoolData as any).alphabeticLunchWaves;
    }

    // Canada-specific: don't send county_code/district_code/region for Canadian schools
    const effectiveCountry = schoolData.country ?? apiData.country;
    if (effectiveCountry !== 'CA') {
      if (schoolData.districtCode !== undefined) apiData.district_code = schoolData.districtCode;
      if (schoolData.countyCode !== undefined) apiData.county_code = schoolData.countyCode;
      if (schoolData.region !== undefined) apiData.region = schoolData.region;
    }

    const { data } = await apiClient.put<School>(
      `/v2/schools/${schoolId}`,
      apiData
    );
    return data;
  },

  /**
   * Enable a feature for a school
   */
  async enableFeature(schoolId: string, featureId: string): Promise<void> {
    await apiClient.post(`/v2/schools/${schoolId}/features/${featureId}`);
  },

  /**
   * Disable a feature for a school
   */
  async disableFeature(schoolId: string, featureId: string): Promise<void> {
    await apiClient.delete(`/v2/schools/${schoolId}/features/${featureId}`);
  },

  /**
   * Upload school logo
   */
  async uploadLogo(file: File): Promise<{ id: string; url: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await apiClient.post<{ id: string; url: string }>(
      '/v2/media/school_logo',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  },

  /**
   * Get logo URL from logoId by fetching the school
   * This is needed because the search API doesn't return logo URLs
   */
  async getLogoUrlFromSchool(schoolId: string): Promise<string | null> {
    try {
      // Fetch the school to get the logo URL
      const school = await this.getSchoolById(schoolId, false);
      return school.logo?.resourceUrl || null;
    } catch (error) {
      // If endpoint doesn't exist or fails, return null
      console.warn('Failed to fetch logo URL for school:', schoolId, error);
      return null;
    }
  },

  // Calendar & Schedule specific methods
  async getCalendar(schoolId: string): Promise<CalendarDayInfo[]> {
    // Use v2/schools/{schoolId}/calendar endpoint like old dashboard
    const { data } = await apiClient.get<CalendarEntry[]>(
      `/v2/schools/${schoolId}/calendar`,
      { params: { dashboard_mode: true } }
    );
    
    // Transform to match old dashboard structure
    return data.map((day: CalendarEntry) => ({
      dateLabel: day.schedule?.name || day.rawBlockName || '',
      scheduleDisplay: day.schedule?.displayName || day.rawBlockName || '',
      dateNow: day.date,
      isCanceled: day.isCanceled,
      scheduleId: day.schedule?.id || null,
      modified: day.schedule ? !day.schedule.special && !day.schedule.static : false,
      special: day.schedule?.special || false,
    }));
  },

  async updateDaySchedule(
    schoolId: string,
    date: string,
    scheduleId: string,
    scheduleName?: string
  ) {
    // Vacation/break codes start with # - they need special handling
    const isVacationCode = scheduleId.startsWith('#');
    
    if (isVacationCode) {
      // For vacation codes, use scheduleId "0" in URL and pass the code as custom_schedule
      const { data } = await apiClient.patch(
        `/v3/schedules/calendar/${schoolId}/${date}/schedule/0`,
        { custom_schedule: scheduleId }
      );
      return data;
    } else {
      // Regular schedule update
      const { data } = await apiClient.patch(
        `/v3/schedules/calendar/${schoolId}/${date}/schedule/${scheduleId}`,
        scheduleName ? { custom_schedule: scheduleName } : {}
      );
      return data;
    }
  },

  async cancelDay(schoolId: string, date: string) {
    const { data } = await apiClient.patch(
      `/v3/schedules/calendar/${schoolId}/${date}/cancel`
    );
    return data;
  },

  async bulkUpdateCalendar(schoolId: string, calendarData: any[]) {
    const { data } = await apiClient.put(
      `/v3/schedules/calendar/${schoolId}`,
      calendarData
    );
    return data;
  },

  async getSchedules(schoolId: string): Promise<Schedule[]> {
    const { parseScheduleResponse } = await import('@/lib/schedule-utils');
    
    // Fetch schedules, images, and drafts in parallel like old dashboard
    const [schedulesData, imagesData, draftsData] = await Promise.all([
      apiClient.get<ScheduleResponse[]>(`/v3/schedules/${schoolId}`),
      apiClient
        .get<ScheduleImageResponse[]>(`/v3/schedules/images`, {
          params: {
            schedule_ids: [], // Will be populated below
          },
        })
        .catch(() => ({ data: [] })), // Images are optional
      apiClient
        .get<DraftScheduleResponse[]>(`/v2/network/reports/draft_schedules`, {
          params: { school_id: schoolId },
        })
        .catch(() => ({ data: [] })), // Drafts are optional
    ]);

    const schedules = schedulesData.data;
    
    // Fetch images if we have schedules
    let images: ScheduleImageResponse[] = [];
    if (schedules.length > 0) {
      const scheduleIds = schedules.map((s) => s.id).filter(Boolean);
      if (scheduleIds.length > 0) {
        try {
          const imagesParams = scheduleIds.map((id) => `schedule_ids=${id}`).join('&');
          const imagesResponse = await apiClient.get<ScheduleImageResponse[]>(
            `/v3/schedules/images?${imagesParams}`
          );
          images = imagesResponse.data;
        } catch {
          // Images are optional
        }
      }
    }

    // Parse all schedules
    return schedules.map((s) =>
      parseScheduleResponse(s, images, draftsData.data)
    );
  },

  async getSchoolYears(schoolId: string): Promise<SchoolYear[]> {
    return this.getSchoolResource<SchoolYear[]>(schoolId, 'school_years');
  },

  async createSchoolYear(schoolName: string, startDate: string, endDate: string, name?: string, emoji?: string) {
    // Create CSV file for upload endpoint (endpoint only accepts CSV)
    // Format: school_id,start_date,end_date,name,emoji
    // Note: school_id in CSV is actually the school name (schoolName)
    const csvHeaders = 'school_id,start_date,end_date,name,emoji\n';
    const csvRow = `${schoolName},${startDate},${endDate},${name || ''},${emoji || ''}\n`;
    const csvContent = csvHeaders + csvRow;
    
    const csvBlob = new Blob([csvContent], { type: 'text/csv' });
    const file = new File([csvBlob], 'school_year.csv', { type: 'text/csv' });
    
    const formData = new FormData();
    formData.append('file', file);
    
    const { data } = await apiClient.post('/v2/admin/upload/school_years', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  async updateSchoolYear(
    schoolYearId: string,
    schoolName: string,
    startDate: string,
    endDate: string,
    current?: boolean
  ) {
    // API doesn't have update or delete endpoints for school years
    // We can only create new ones via CSV upload
    // So we'll create a new school year with updated dates
    // Note: The old school year will remain in the system
    // The backend may handle duplicates or you may need to clean them up manually
    return this.createSchoolYear(schoolName, startDate, endDate);
  },

  async deleteSchoolYear(schoolYearId: string) {
    // API doesn't support deleting school years
    // This method will throw an error to inform the user
    throw new Error('School year deletion is not supported by the API. Please contact support if you need to remove a school year.');
  },

  async getCourses(
    schoolId: string,
    includeUserGenerated = true
  ): Promise<Course[]> {
    return this.getSchoolResource<Course[]>(
      schoolId,
      'courses',
      true,
      includeUserGenerated
    );
  },

  async getStaff(schoolId: string): Promise<Staff[]> {
    return this.getSchoolResource<Staff[]>(schoolId, 'staff');
  },

  async getLinks(schoolId: string): Promise<SchoolLink[]> {
    return this.getSchoolResource<SchoolLink[]>(schoolId, 'links');
  },

  // Schedule Settings
  async getScheduleSettings(schoolId: string) {
    const { data } = await apiClient.get<ScheduleSettingsResponse>(
      `/v3/schedules/settings/${schoolId}`
    );
    return data;
  },

  async updateRotationPattern(schoolId: string, rotationPattern: string[]) {
    const { data } = await apiClient.put(`/v2/schools/${schoolId}`, {
      rotationPattern,
    });
    return data;
  },

  async updateScheduleType(schoolId: string, scheduleType: string) {
    const { data } = await apiClient.put(`/v2/schools/${schoolId}`, {
      scheduleType,
    });
    return data;
  },

  async updateLunchSlots(schoolId: string, numSlots: number) {
    const { data } = await apiClient.patch(`/v3/schedules/lunch_slots`, {
      schoolId,
      slots: numSlots,
    });
    return data;
  },

  async createLunchClass(schoolId: string) {
    const { data } = await apiClient.post<Course>(`/v2/courses`, {
      name: 'Lunch',
      school: schoolId,
      emoji_id: 'burger',
    });
    return data;
  },

  async deleteLunchClass(courseId: string) {
    await apiClient.delete(`/v2/courses/${courseId}`);
  },

  // Day Types
  async createDayType(schoolId: string, name: string) {
    const { data } = await apiClient.post('/v3/schedules/day_types', {
      schoolId,
      name,
    });
    return data;
  },

  async updateDayType(dayTypeId: string, name: string) {
    const { data } = await apiClient.patch(`/v3/schedules/day_types/${dayTypeId}`, {
      name,
    });
    return data;
  },

  async deleteDayType(dayTypeId: string) {
    await apiClient.delete(`/v3/schedules/day_types/${dayTypeId}`);
  },

  // Period Types
  async createPeriodType(schoolId: string, name: string) {
    const { data } = await apiClient.post('/v3/schedules/period_types', {
      schoolId,
      name,
    });
    return data;
  },

  async updatePeriodType(periodTypeId: string, name: string) {
    const { data } = await apiClient.patch(`/v3/schedules/period_types/${periodTypeId}`, {
      name,
    });
    return data;
  },

  async deletePeriodType(periodTypeId: string) {
    await apiClient.delete(`/v3/schedules/period_types/${periodTypeId}`);
  },

  // Schedule CRUD operations
  async createSchedule(schoolId: string, schedule: Partial<Schedule>, timezone?: string) {
    // Get school timezone if not provided
    if (!timezone) {
      const school = await this.getSchoolById(schoolId, false);
      timezone = school.timezone;
    }
    
    // Transform to API format
    const requestData = transformScheduleToRequest(schoolId, schedule, timezone);
    const { data } = await apiClient.post<ScheduleResponse>(
      `/v3/schedules`,
      requestData
    );
    const { parseScheduleResponse } = await import('@/lib/schedule-utils');
    return parseScheduleResponse(data);
  },

  async updateSchedule(schoolId: string, scheduleId: string, schedule: Partial<Schedule>, timezone?: string) {
    // Get school timezone if not provided
    if (!timezone) {
      const school = await this.getSchoolById(schoolId, false);
      timezone = school.timezone;
    }
    
    // Transform to API format
    const requestData = transformScheduleToRequest(schoolId, schedule, timezone);
    const { data } = await apiClient.patch<ScheduleResponse>(
      `/v3/schedules/${scheduleId}`,
      requestData
    );
    const { parseScheduleResponse } = await import('@/lib/schedule-utils');
    return parseScheduleResponse(data);
  },

  async deleteSchedule(schoolId: string, scheduleId: string) {
    await apiClient.delete(`/v3/schedules/${scheduleId}`);
  },

  // AI Schedule Image Upload and Parsing
  /**
   * Initiate image upload and get presigned POST data for S3
   */
  async initiateImageUpload(fileType: string): Promise<{ imageId: string; url: string; fields: Record<string, string> }> {
    // Note: API client auto-converts snake_case to camelCase
    const { data } = await apiClient.post<{ imageId: string; url: string; fields: Record<string, string> }>(
      `/v2/media/image-upload/initiate`,
      null,
      {
        params: { 'file-type': fileType },
      }
    );
    
    return data;
  },

  /**
   * Upload image to S3 using presigned POST
   */
  async uploadImageToS3(url: string, fields: Record<string, string>, file: File): Promise<void> {
    const formData = new FormData();
    
    // Add all the presigned fields first
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    
    // Add the file last (required by S3)
    formData.append('file', file);
    
    // S3 presigned POST returns 204 on success but may not include CORS headers
    // causing fetch to throw even though upload succeeded. Use no-cors mode.
    await fetch(url, {
      method: 'POST',
      body: formData,
      mode: 'no-cors', // S3 doesn't return CORS headers, but upload still works
    });
    
    // With no-cors mode, we can't check response status, but S3 will error
    // on the AI endpoint if upload failed, so we proceed optimistically
  },

  /**
   * Parse schedule from uploaded image IDs using AI
   * This endpoint takes ~20 seconds to respond with parsed schedules
   */
  async parseScheduleFromImageIds(imageIds: string[]): Promise<AIParsedSchedule[]> {
    const { data } = await apiClient.post<AIParseFromImageResponse>(
      `/v1/ai/schedules/from_image_ids`,
      { image_ids: imageIds }
    );
    
    // Response is wrapped in scheduleData (snake_case converted to camelCase)
    const schedules = data.scheduleData?.schedules;
    if (!schedules || schedules.length === 0) {
      return [];
    }

    return schedules.map((schedule, index) => {
      // Extract lunches from periods that have lunch arrays
      const allLunches: AIParsedLunch[] = [];
      const periods: AIParsedPeriod[] = [];
      
      (schedule.periods || []).forEach(p => {
        // Add the period
        periods.push({
          name: p.period || '',
          startTime: p.start || '',
          endTime: p.end || '',
        });
        
        // If period has lunch waves, extract them
        if (p.lunch && p.lunch.length > 0) {
          p.lunch.forEach(l => {
            allLunches.push({
              name: l.wave || '',
              startTime: l.start || '',
              endTime: l.end || '',
            });
          });
        }
      });

      return {
        genId: data.genId || '',
        id: `${data.genId || 'parsed'}-${index}`,
        url: '',
        title: schedule.title || `Schedule ${index + 1}`,
        periods,
        lunches: allLunches,
        isComplete: true,
      };
    });
  },

  /**
   * Upload image and parse schedule in one call
   * 1. Initiate upload to get presigned POST data
   * 2. Upload to S3
   * 3. Call AI endpoint and wait for response (~20 seconds)
   */
  async uploadAndParseScheduleImage(file: File): Promise<AIParsedSchedule[]> {
    // Extract extension from MIME type (e.g., "image/jpeg" -> "jpeg")
    const fileExtension = file.type.split('/')[1] || 'jpeg';
    
    // Step 1: Initiate upload and get presigned POST data
    const { imageId, url, fields } = await this.initiateImageUpload(fileExtension);
    
    // Step 2: Upload to S3 using presigned POST
    await this.uploadImageToS3(url, fields, file);
    
    // Step 3: Call AI endpoint (takes ~20 seconds)
    const parsedSchedules = await this.parseScheduleFromImageIds([imageId]);
    
    return parsedSchedules;
  },

  /**
   * Convert an AI-parsed schedule to a partial Schedule object for the form
   */
  convertParsedScheduleToFormData(
    parsedSchedule: AIParsedSchedule,
    schoolId: string,
    school?: { 
      dayTypes?: Array<{ id: string; name: string }>; 
      periodTypes?: Array<{ id: string; name: string }>;
      lunchSettings?: { type: 'unit' | 'class' | 'waves'; slots?: Array<{ id: string; slot: number }>; isAlphabetic?: boolean };
    }
  ): Partial<Schedule> {
    // Helper function to match period names more flexibly
    const findMatchingPeriodType = (periodName: string) => {
      if (!school?.periodTypes) return null;
      
      const normalizedName = periodName.toLowerCase().trim();
      
      // Try exact match first
      let match = school.periodTypes.find(
        pt => pt.name.toLowerCase() === normalizedName
      );
      if (match) return match;
      
      // Try matching just the number/letter (e.g., "1" matches "Period 1" or "1st Period")
      for (const pt of school.periodTypes) {
        const ptNormalized = pt.name.toLowerCase();
        // Extract number or letter from period type name
        const ptNumber = ptNormalized.match(/\d+|[a-z](?![a-z])/);
        const periodNumber = normalizedName.match(/\d+|[a-z](?![a-z])/);
        
        if (ptNumber && periodNumber && ptNumber[0] === periodNumber[0]) {
          return pt;
        }
      }
      
      return null;
    };

    // Helper function to find matching day type from schedule title
    const findMatchingDayType = () => {
      if (!school?.dayTypes) return null;
      
      const titleNormalized = parsedSchedule.title.toLowerCase().trim();
      
      // Try to find a day type that matches the schedule title
      // Check if title contains the day type name (e.g., "A Day" contains "A")
      for (const dt of school.dayTypes) {
        const dayTypeNormalized = dt.name.toLowerCase();
        
        // Exact match
        if (titleNormalized === dayTypeNormalized) {
          return dt;
        }
        
        // Title contains day type (e.g., "A Day" contains "A")
        // Use word boundaries to avoid partial matches
        const regex = new RegExp(`\\b${dayTypeNormalized}\\b`, 'i');
        if (regex.test(parsedSchedule.title)) {
          return dt;
        }
      }
      
      // Fall back to first day type if no match found
      return school.dayTypes[0] || null;
    };

    const canConfigurePeriodTypes = school?.periodTypes && school.periodTypes.length > 0;
    const dayType = findMatchingDayType();

    // Get available lunch slots from school settings
    const lunchSlots = school?.lunchSettings?.type === 'waves' && school.lunchSettings.slots 
      ? school.lunchSettings.slots 
      : [];

    return {
      schoolId,
      genId: parsedSchedule.genId,
      displayName: parsedSchedule.title,
      variant: 'core', // Default to core, user can change
      periods: parsedSchedule.periods.map((p, index) => {
        const basePeriod = {
          id: `parsed-${index}`,
          name: p.name,
          startTime: parseTimeToHHMM(p.startTime),
          endTime: parseTimeToHHMM(p.endTime),
        };

        // If we have period types configured and can find a match, assign day type and period type
        if (canConfigurePeriodTypes && dayType) {
          const matchingPeriodType = findMatchingPeriodType(p.name);
          
          if (matchingPeriodType) {
            return {
              ...basePeriod,
              name: matchingPeriodType.name,
              dayType: { id: dayType.id, name: dayType.name },
              periodType: { id: matchingPeriodType.id, name: matchingPeriodType.name },
            };
          }
        }

        // For non-matching periods, return without day/period types (custom period)
        return basePeriod;
      }) as any,
      lunchWaves: parsedSchedule.lunches.map((l, index) => {
        // Assign proper slot from school's lunch settings if available
        const slot = lunchSlots[index] || { id: '', slot: index + 1 };
        return {
          id: `parsed-lunch-${index}`,
          name: l.name,
          startTime: parseTimeToHHMM(l.startTime),
          endTime: parseTimeToHHMM(l.endTime),
          slot,
        };
      }).filter((_, index) => index < lunchSlots.length || lunchSlots.length === 0) as any, // Only include waves if slots exist
      isDraft: false,
      isUserDraft: false,
      image: parsedSchedule.url ? {
        source: 'ai',
        url: parsedSchedule.url,
      } : undefined,
    };
  },

  /**
   * Get schools GeoJSON data for map display
   */
  async getSchoolsGeoJSON(filters?: string) {
    const { data } = await apiClient.get('/v2/map/schools/geojson', {
      params: filters ? { filter_by: filters } : undefined,
    });
    
    // Transform the response to match the expected format
    // API returns: { map_features: [...], total_schools, total_installs, etc. }
    // We need to filter by status and create separate GeoJSON collections
    const filterFeatures = (features: any[], status: string) => {
      const filtered = features?.filter(
        (feature: any) => feature.properties?.status === status
      ) || [];
      return {
        type: 'FeatureCollection',
        features: filtered,
      };
    };

    const mapFeatures = data.mapFeatures || [];
    
    return {
      live: filterFeatures(mapFeatures, 'live'),
      waitlist: filterFeatures(mapFeatures, 'waitlist'),
      suspended: filterFeatures(mapFeatures, 'suspended'),
      unlaunched: filterFeatures(mapFeatures, 'unlaunched'),
      stats: {
        totalSchools: data.totalSchools || 0,
        totalInstalls: data.totalInstalls || 0,
        totalLive: data.totalLive || 0,
        totalWaitlist: data.totalWaitlist || 0,
        totalUnlaunched: data.totalUnlaunched || 0,
        totalSuspended: data.totalSuspended || 0,
      },
    };
  },
};

// Helper to parse time from various formats to HH:mm
const parseTimeToHHMM = (time: string): string => {
  // If already in HH:mm format, return as is
  if (/^\d{2}:\d{2}$/.test(time)) {
    return time;
  }
  
  // Parse 12-hour format (e.g., "8:30 AM", "12:00 PM")
  const match = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?/i);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3]?.toUpperCase();
    
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }
  
  return time;
};

// Ensure time is in HH:mm:ss format (API requires seconds)
const formatTimeForApi = (time: string): string => {
  if (!time) return time;
  // If already HH:mm:ss, return as-is
  if (/^\d{2}:\d{2}:\d{2}$/.test(time)) return time;
  // If HH:mm, append :00 for seconds
  if (/^\d{2}:\d{2}$/.test(time)) return `${time}:00`;
  return time;
};

// Helper to transform Schedule object to API request format
function transformScheduleToRequest(schoolId: string, schedule: Partial<Schedule>, timezone: string): any {
  const base: any = {
    schoolId,
    displayName: schedule.displayName,
    draft: schedule.isDraft || false,
    userDraft: schedule.isUserDraft || false,
    ...(schedule.genId && { genId: schedule.genId }),
    periods: schedule.periods?.map((p) => ({
      name: p.name,
      startTime: formatTimeForApi(p.startTime),
      endTime: formatTimeForApi(p.endTime),
      periodTypeId: ('periodType' in p && p.periodType) ? p.periodType.id : null,
      dayTypeId: ('periodType' in p && p.periodType?.id && 'dayType' in p) ? p.dayType?.id : null,
      timezone,
    })),
    lunchWaves: schedule.lunchWaves
      ?.filter((w) => w.slot?.id && w.slot.id !== '') // Only include waves with valid slot IDs
      ?.map((w) => ({
        name: w.name,
        startTime: formatTimeForApi(w.startTime),
        endTime: formatTimeForApi(w.endTime),
        slotId: w.slot.id,
        timezone,
      })),
  };

  if (!schedule.variant) return base;

  // Variant-specific fields
  if (schedule.variant === 'core' && schedule.dayType) {
    const grid = schedule.isInAcf;
    // API requires order when grid is true - use 0 as default if not provided or null
    const order = (schedule.order !== undefined && schedule.order !== null) ? schedule.order : 0;
    
    return {
      ...base,
      name: schedule.dayOfWeek 
        ? `${schedule.dayType.name}!${schedule.dayOfWeek}`
        : schedule.dayType.name,
      dayTypeId: schedule.dayType.id,
      static: true,
      special: false,
      hidden: !schedule.isInLsc,
      grid,
      // Only include order if grid is true (API requirement)
      ...(grid && { order }),
    };
  }

  if (schedule.variant === 'modified' && schedule.dayType) {
    const modification = schedule.modification === 'OTHER' && schedule.otherModification
      ? schedule.otherModification
      : schedule.modification;
    
    return {
      ...base,
      name: `${schedule.dayType.name}!${modification}`,
      dayTypeId: schedule.dayType.id,
      static: false,
      special: false,
      hidden: !schedule.isInLsc,
    };
  }

  if (schedule.variant === 'special' && schedule.date) {
    const year = schedule.date.getFullYear();
    const month = String(schedule.date.getMonth() + 1).padStart(2, '0');
    const day = String(schedule.date.getDate()).padStart(2, '0');
    return {
      ...base,
      name: `#-${month}-${day}-${year}`,
      onDate: `${year}-${month}-${day}`,
      static: false,
      special: true,
      hidden: true,
    };
  }

  return base;
}

